// ModuleView — the ONE shared way to display a course (modul) as a READ view.
//
// Sibling of `needs/NeedView` — but deliberately NOT a look-alike. A job posting
// is a spec sheet (facts in a tile grid); a course is a LEARNING PATH, so its
// identity is the curriculum. This renders the syllabus as a connected vertical
// timeline (grouped by phase) so a course reads as a course, not a job. Shares
// the design system (icon chip, cards, action slot) for coherence, not the
// silhouette. NOT the interactive player (that's `ModuleViewer`).
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
    BookOpen, Clock, ListChecks, Info, CheckCircle2, ShieldCheck, HelpCircle,
    Briefcase, FileText, ListOrdered, ChevronDown, ChevronUp,
} from 'lucide-react';
import { CourseAvatar } from './CourseAvatar';
import type { LucideIcon } from 'lucide-react';
import type { ModuleFullResponse, ModuleStep, StepType } from '@/lib/models/module';
import ModuleStepContent, { hasStepContent } from './ModuleStepContent';

// step_type → read-view label + icon (display order matches the player).
const STEP_META: Record<StepType, { label: string; icon: LucideIcon }> = {
    info: { label: 'Information', icon: Info },
    task: { label: 'Uppgift', icon: ListChecks },
    checkpoint: { label: 'Kontroll', icon: CheckCircle2 },
    confirmation: { label: 'Bekräftelse', icon: ShieldCheck },
    quiz: { label: 'Quiz', icon: HelpCircle },
    case: { label: 'Case', icon: Briefcase },
    file_review: { label: 'Filgranskning', icon: FileText },
};

const stepMeta = (t: StepType) => STEP_META[t] ?? { label: t, icon: Info };

// ── Adapter ─────────────────────────────────────────────────────────
export interface ModuleViewData {
    name: string;
    description?: string;
    estimatedDuration?: string;
    steps: ModuleStep[];
    organisationName?: string;      // #40
    organisationLogoUrl?: string;   // #40
}

export const moduleToView = (m: ModuleFullResponse): ModuleViewData => ({
    name: m.module_name,
    description: m.module_description,
    estimatedDuration: m.estimated_duration,
    steps: [...(m.steps || [])].sort((a, b) => a.step_order - b.step_order),
    organisationName: m.organisation_name,
    organisationLogoUrl: m.organisation_logo_url,
});

// Small header meta pill (duration, step count) — deliberately NOT the job
// posting's stat-tile grid.
const MetaPill: React.FC<{ icon: LucideIcon; children: React.ReactNode }> = ({ icon: Icon, children }) => (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#A4C3B2]/20 text-[#1A4D4F]">
        <Icon className="w-3.5 h-3.5" /> {children}
    </span>
);

// Group consecutive steps that share a non-empty `phase` under one section head.
interface Section { phase?: string; steps: ModuleStep[] }
const toSections = (steps: ModuleStep[]): Section[] => {
    const sections: Section[] = [];
    for (const step of steps) {
        const phase = step.phase?.trim() || undefined;
        const last = sections[sections.length - 1];
        if (last && last.phase === phase) last.steps.push(step);
        else sections.push({ phase, steps: [step] });
    }
    return sections;
};

interface ModuleViewProps {
    data: ModuleViewData;
    actions?: React.ReactNode;   // header-right buttons (mode-specific)
    children?: React.ReactNode;  // extra sections (danger zone, etc.)
    /** Start with every step's content expanded (e.g. Link's editing preview,
     *  where seeing the content matters more than a compact outline). */
    defaultExpanded?: boolean;
}

const ModuleView: React.FC<ModuleViewProps> = ({ data, actions, children, defaultExpanded }) => {
    const sections = toSections(data.steps);
    const stepCount = data.steps.length;
    // Display numbering by position in the (already sorted) list — robust to
    // whether step_order is 0- or 1-indexed.
    const numberById = new Map(data.steps.map((s, i) => [s.id, i + 1]));

    // Expand/collapse per step to reveal its content.
    const [expanded, setExpanded] = useState<Set<string>>(() =>
        defaultExpanded
            ? new Set(data.steps.filter((s) => hasStepContent(s.step_type, s.content)).map((s) => s.id))
            : new Set(),
    );
    const toggle = (id: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    return (
        <div className="space-y-5">
            {/* Header — icon chip + name + inline meta PILLS (not a job-style tile grid).
                Stacks on mobile so the action buttons never overflow the title. */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                <div className="flex items-start gap-3 min-w-0">
                    <CourseAvatar organisationName={data.organisationName} organisationLogoUrl={data.organisationLogoUrl} size="xl" />
                    <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#1A4D4F]/70">Modul</p>
                        <h1 className="text-xl font-bold text-gray-900">{data.name || 'Namnlös modul'}</h1>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            <MetaPill icon={ListOrdered}>{stepCount} steg</MetaPill>
                            {data.estimatedDuration && <MetaPill icon={Clock}>{data.estimatedDuration}</MetaPill>}
                        </div>
                    </div>
                </div>
                {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
            </div>

            {/* Description */}
            {data.description && (
                <div className="prose prose-sm max-w-none text-gray-700 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h2]:mt-5 [&_h2]:mb-2">
                    <ReactMarkdown>{data.description}</ReactMarkdown>
                </div>
            )}

            {/* Curriculum — a connected learning path (the course's identity) */}
            {stepCount > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <ListChecks className="w-4 h-4 text-[#1A4D4F]" /> Modulinnehåll
                    </h3>
                    <div className="space-y-5">
                        {sections.map((section, si) => (
                            <div key={si}>
                                {section.phase && (
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">{section.phase}</p>
                                )}
                                <ol className="relative space-y-3">
                                    {/* the vertical path line, centred under the nodes */}
                                    {section.steps.length > 1 && (
                                        <span className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-[#A4C3B2]/50" aria-hidden />
                                    )}
                                    {section.steps.map((step) => {
                                        const meta = stepMeta(step.step_type);
                                        const Icon = meta.icon;
                                        const canExpand = hasStepContent(step.step_type, step.content);
                                        const isOpen = expanded.has(step.id);
                                        return (
                                            <li key={step.id} className="relative flex items-start gap-3">
                                                {/* node on the path */}
                                                <span className="relative z-[1] flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-white border-2 border-[#1A4D4F] text-[#1A4D4F] text-xs font-bold">
                                                    {numberById.get(step.id)}
                                                </span>
                                                <div className="min-w-0 flex-1 rounded-xl border border-gray-100 bg-white overflow-hidden">
                                                    <button
                                                        type="button"
                                                        onClick={() => canExpand && toggle(step.id)}
                                                        aria-expanded={isOpen}
                                                        className={`w-full flex items-center gap-2 p-3 text-left ${canExpand ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'}`}
                                                    >
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#A4C3B2]/20 text-[#1A4D4F] flex-shrink-0">
                                                            <Icon className="w-3 h-3" /> {meta.label}
                                                        </span>
                                                        <span className="text-sm font-medium text-gray-900 flex-1 min-w-0">{step.step_title || meta.label}</span>
                                                        {canExpand && (
                                                            isOpen
                                                                ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                                : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                        )}
                                                    </button>
                                                    {isOpen && canExpand && (
                                                        <div className="px-3 pb-3 pt-2 border-t border-gray-100">
                                                            <ModuleStepContent stepType={step.step_type} content={step.content} />
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ol>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {children}
        </div>
    );
};

export default ModuleView;
